import {
  FormEvent,
  ReactNode,
  Suspense,
  lazy,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Activity,
  ArrowLeft,
  AtSign,
  BarChart3,
  Bot,
  BriefcaseBusiness,
  CheckCircle2,
  FileText,
  Inbox,
  Landmark,
  ListTodo,
  LockKeyhole,
  LogOut,
  Mail,
  PhoneCall,
  Repeat2,
  ReceiptText,
  Search,
  Send,
  ShieldCheck,
  UserCheck,
  UserPlus,
  Users,
  WalletCards,
} from 'lucide-react';

import type {
  CaseNoteResponse,
  CaseWorkflowEvent,
  CaseWorkspaceResponse,
  ClientCareContextResponse,
  EmailItem,
  OperationAuditEvent,
  OperationCase,
  OperationMessage,
  OperationTask,
  OperationsDashboard,
  OperationsRequestContext,
  PageResponse,
  ReconciliationRun,
  RegulatoryReportDefinition,
  RegulatoryReportRun,
  TenantRole,
  TenantUser,
} from '../api/operations-service';
import type {
  CaseAssignmentPayload,
  CaseHumanResponseMutation,
  CaseNote,
  CaseQueueMutation,
  CaseUpdateMutation,
  KycAmlAccountBlock,
  KycAmlCase,
  KycAmlCheck,
  KycAmlClientAccount,
  KycAmlDocument,
  KycAmlFilters,
  KycAmlOverride,
  NavKey,
  OpsSession,
  QueryState,
} from './types';
import {
  getTenantOperationsConfiguration,
  isPrivateEquityEnabled,
  type TenantOperationsConfiguration,
} from '../services/platformTenants.service';
import { api } from './api-client';
import {
  ACCESS_TOKEN_KEY,
  SESSION_KEY,
  caseQueueOptions,
  clientCareCaseTypes,
  metricStripSx,
  navItems,
  priorityOptions,
  roleOptions,
  statusOptions,
  workflowSteps,
} from './constants';
import {
  demoAccountsForKycCase,
  demoAiTrace,
  demoAuditEvents,
  demoCases,
  demoClientCareContext,
  demoDashboard,
  demoDocuments,
  demoEmailItems,
  demoFilteredCases,
  demoFilteredEmailItems,
  demoFilteredKycAmlCases,
  demoIso,
  demoKycAmlCases,
  demoPage,
  demoReconciliationRuns,
  demoReports,
  demoRoles,
  demoSession,
  demoTasks,
  demoUsers,
  isDemoLogin,
} from './demo-data';
import {
  actorPayload,
  addToPage,
  asRecord,
  buildSessionFromToken,
  canAccessNavItem,
  canSubmitKycDecision,
  canSupervisorApproveKycDecision,
  caseFilterParams,
  compactDate,
  compareOperationalRisk,
  countByValue,
  errorMessage,
  extractAuthToken,
  extractCaseNotes,
  extractClaimsToken,
  firstFromKeys,
  firstText,
  firstValueFromKeys,
  fullName,
  getPath,
  highPriority,
  isDueSoon,
  isPastDue,
  labelize,
  mapInvestorAccountToKycAccount,
  mapInvestorAccountsToKycAccounts,
  mapInvestorDocumentsToKycDocuments,
  mapQuinAccountBlocks,
  mapQuinCaseToKycAmlCase,
  mapQuinChecksToKycChecks,
  mapQuinDocumentsToKycDocuments,
  mapQuinOverrides,
  mergeAccountBlocksFromAccounts,
  moneyText,
  normaliseList,
  normalisePage,
  persistSession,
  quinSearchPayload,
  randomDemoId,
  readSession,
  shortId,
  sortedMetricEntries,
  sumMetricEntries,
  terminalCaseStatus,
  updateCaseCaches,
  updateListItem,
  updatePageItem,
  workflowStepIndex,
} from './operations-utils';
const OverviewView = lazy(() =>
  import('./overview-view').then((module) => ({
    default: module.OverviewView,
  })),
);
const ClientCareView = lazy(() =>
  import('./client-care-view').then((module) => ({
    default: module.ClientCareView,
  })),
);
const AccountManagementView = lazy(() =>
  import('./account-management-view').then((module) => ({
    default: module.AccountManagementView,
  })),
);
const UsersView = lazy(() =>
  import('./users-view').then((module) => ({ default: module.UsersView })),
);
const ConfigurationView = lazy(() =>
  import('./configuration-view').then((module) => ({
    default: module.ConfigurationView,
  })),
);
const FeeChargesView = lazy(() =>
  import('./fee-charges-view').then((module) => ({
    default: module.FeeChargesView,
  })),
);
const CasesView = lazy(() =>
  import('./cases-view').then((module) => ({ default: module.CasesView })),
);
const KycAmlWorkspace = lazy(() =>
  import('./kyc-aml-workspace').then((module) => ({
    default: module.KycAmlWorkspace,
  })),
);
const AmlWorkspace = lazy(() =>
  import('./aml-workspace').then((module) => ({
    default: module.AmlWorkspace,
  })),
);
const CaseDetailPage = lazy(() =>
  import('./case-detail-page').then((module) => ({
    default: module.CaseDetailPage,
  })),
);
const TasksReportsView = lazy(() =>
  import('./tasks-reports-view').then((module) => ({
    default: module.TasksReportsView,
  })),
);
const ReconciliationView = lazy(() =>
  import('./reconciliation-view').then((module) => ({
    default: module.ReconciliationView,
  })),
);
const BroadcastNoticesView = lazy(() =>
  import('./broadcast-notices-view').then((module) => ({
    default: module.BroadcastNoticesView,
  })),
);
const PlatformWorkspace = lazy(() =>
  import('./platform/platform-workspace').then((module) => ({
    default: module.PlatformWorkspace,
  })),
);
const CapitalRaiseView = lazy(() =>
  import('./capital-raise-view').then((module) => ({
    default: module.CapitalRaiseView,
  })),
);
const IDLE_TIMEOUT_SECONDS = 10 * 60;
const IDLE_TIMEOUT_MS = IDLE_TIMEOUT_SECONDS * 1000;
const IDLE_WARNING_SECONDS = 120;
const IDLE_WARNING_MS = IDLE_WARNING_SECONDS * 1000;

export function OperationsShell({
  session,
  onLogout,
  onSessionRefresh,
}: Readonly<{
  session: OpsSession;
  onLogout: () => void;
  onSessionRefresh: (session: OpsSession) => void;
}>) {
  const [active, setActive] = useState<NavKey>(() =>
    session.actorRoles.map((role) => role.toUpperCase()).includes('ROOT')
      ? 'platform'
      : 'overview',
  );
  const [caseDetailId, setCaseDetailId] = useState<string | null>(null);
  const [clientCareInitialSearch, setClientCareInitialSearch] = useState<Record<
    string,
    string
  > | null>(null);
  const [caseFilters, setCaseFilters] = useState({
    q: '',
    status: '',
    role: '',
  });
  const [casePageIndex, setCasePageIndex] = useState(0);
  const [casePageSize, setCasePageSize] = useState(25);
  const [reconciliationTab, setReconciliationTab] = useState<'eod' | 'parties'>(
    'eod',
  );
  const [reconciliationPageIndex, setReconciliationPageIndex] = useState(0);
  const [reconciliationPageSize, setReconciliationPageSize] = useState(25);
  const [capitalRaiseViewKey, setCapitalRaiseViewKey] = useState(0);
  const [partiesReconciliationPageIndex, setPartiesReconciliationPageIndex] =
    useState(0);
  const [partiesReconciliationPageSize, setPartiesReconciliationPageSize] =
    useState(25);
  const [idleWarningOpen, setIdleWarningOpen] = useState(false);
  const [idleWarningSecondsRemaining, setIdleWarningSecondsRemaining] =
    useState(IDLE_WARNING_SECONDS);
  const lastActivityAtRef = useRef(Date.now());
  const idleWarningOpenRef = useRef(false);
  const idleWarningExpiresAtRef = useRef<number | null>(null);
  const onLogoutRef = useRef(onLogout);

  const context = useMemo<OperationsRequestContext>(
    () => ({
      tenantId: session.tenantId,
      tenantCode: session.tenantCode,
      actorId: session.actorId,
      actorRole: session.actorRole,
      actorRoles: session.actorRoles,
    }),
    [
      session.actorId,
      session.actorRole,
      session.actorRoles,
      session.tenantCode,
      session.tenantId,
    ],
  );
  const isDemo = Boolean(session.isDemo);
  const isRootUser = session.actorRoles
    .map((role) => role.toUpperCase())
    .includes('ROOT');
  const tenantProfileQuery = useQuery<TenantOperationsConfiguration>({
    queryKey: ['platform', 'tenants', session.tenantId, 'configuration'],
    queryFn: () => getTenantOperationsConfiguration(session.tenantId),
    enabled: Boolean(session.tenantId) && !isRootUser && !isDemo,
  });
  const tenantProfile = tenantProfileQuery.data;
  const wealthPlatformEnabled = tenantProfile?.wealthPlatformEnabled !== false;
  const privateEquityEnabled = isPrivateEquityEnabled(tenantProfile);
  const visibleNavItems = useMemo(
    () =>
      navItems.filter((item) => {
        if (!canAccessNavItem(item, session.actorRoles)) {
          return false;
        }
        if (item.platform === 'privateEquity') {
          return privateEquityEnabled;
        }
        if (isRootUser || item.platform === 'shared') {
          return true;
        }
        return wealthPlatformEnabled;
      }),
    [
      privateEquityEnabled,
      isRootUser,
      session.actorRoles,
      wealthPlatformEnabled,
    ],
  );
  const hasVisibleNavItem = (key: NavKey) =>
    visibleNavItems.some((item) => item.key === key);
  const canManageUsers = visibleNavItems.some((item) => item.key === 'users');
  const canUseTenantUsers =
    canManageUsers ||
    visibleNavItems.some(
      (item) => item.key === 'cases' || item.key === 'tasks',
    );
  const actorRoleLabels = useMemo(() => {
    const roles = session.actorRoles.length
      ? session.actorRoles
      : [session.actorRole].filter(Boolean);
    return Array.from(new Set(roles.map(labelize).filter(Boolean)));
  }, [session.actorRole, session.actorRoles]);
  const displayName =
    [session.givenName, session.familyName].filter(Boolean).join(' ') ||
    session.username.split('@')[0] ||
    session.username;
  const displayInitials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');

  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  const refreshPath = import.meta.env.VITE_OPS_REFRESH_PATH || undefined;
  const refreshSessionMutation = useMutation({
    mutationFn: async () => {
      if (session.isDemo) {
        return session;
      }
      if (!refreshPath) {
        return session;
      }

      const response = await api.refreshSession(
        { token: session.authToken || session.token },
        refreshPath,
      );
      const authToken = extractAuthToken(response.data) || session.token;
      const claimsToken = extractClaimsToken(response.data) || authToken;
      return buildSessionFromToken(
        claimsToken,
        session.username,
        authToken,
        response.data,
      );
    },
    onSuccess: (nextSession) => {
      onSessionRefresh(nextSession);
      lastActivityAtRef.current = Date.now();
      idleWarningExpiresAtRef.current = null;
      idleWarningOpenRef.current = false;
      setIdleWarningOpen(false);
      setIdleWarningSecondsRemaining(IDLE_WARNING_SECONDS);
    },
    onError: () => {
      onLogoutRef.current();
    },
  });

  useEffect(() => {
    const activityEvents: (keyof WindowEventMap)[] = [
      'click',
      'keydown',
      'mousemove',
      'pointerdown',
      'scroll',
      'touchstart',
    ];

    const markActivity = () => {
      if (idleWarningOpenRef.current) {
        return;
      }
      lastActivityAtRef.current = Date.now();
    };

    const updateCountdown = () => {
      if (idleWarningOpenRef.current && idleWarningExpiresAtRef.current) {
        const warningRemainingMs = idleWarningExpiresAtRef.current - Date.now();
        if (warningRemainingMs <= 0) {
          onLogoutRef.current();
          return;
        }
        setIdleWarningSecondsRemaining(Math.ceil(warningRemainingMs / 1000));
        return;
      }

      const idleMs = Date.now() - lastActivityAtRef.current;
      if (idleMs >= IDLE_TIMEOUT_MS) {
        idleWarningExpiresAtRef.current = Date.now() + IDLE_WARNING_MS;
        idleWarningOpenRef.current = true;
        setIdleWarningOpen(true);
        setIdleWarningSecondsRemaining(IDLE_WARNING_SECONDS);
      }
    };

    activityEvents.forEach((eventName) =>
      window.addEventListener(eventName, markActivity, { passive: true }),
    );
    const intervalId = window.setInterval(updateCountdown, 1000);
    updateCountdown();

    return () => {
      activityEvents.forEach((eventName) =>
        window.removeEventListener(eventName, markActivity),
      );
      window.clearInterval(intervalId);
    };
  }, []);

  const handleContinueSession = () => {
    if (refreshSessionMutation.isPending) {
      return;
    }
    refreshSessionMutation.mutate();
  };

  useEffect(() => {
    if (!visibleNavItems.some((item) => item.key === active)) {
      setActive(visibleNavItems[0]?.key ?? 'overview');
      setCaseDetailId(null);
    }
  }, [active, visibleNavItems]);

  const dashboardQuery = useQuery({
    queryKey: ['operations-dashboard', context],
    queryFn: async () =>
      isDemo ? demoDashboard : (await api.dashboard(context)).data,
    enabled: !isRootUser && hasVisibleNavItem('overview'),
  });

  const dashboardOpenCasesQuery = useQuery({
    queryKey: ['operations-dashboard-open-cases', context],
    queryFn: async () => {
      if (isDemo) {
        const openCases = demoFilteredCases({
          q: '',
          status: 'OPEN_CASES',
          role: '',
        });
        return demoPage(openCases.slice(0, 25), 25, openCases.length);
      }
      return normalisePage<OperationCase>(
        (await api.dashboardOpenCases(context, 0, 25)).data,
      );
    },
    enabled: !isRootUser && hasVisibleNavItem('overview'),
  });

  const casesQuery = useQuery({
    queryKey: [
      'operations-cases',
      context,
      caseFilters,
      casePageIndex,
      casePageSize,
    ],
    queryFn: async () => {
      if (isDemo) {
        const filteredCases = demoFilteredCases(caseFilters);
        return demoPage(
          filteredCases.slice(
            casePageIndex * casePageSize,
            (casePageIndex + 1) * casePageSize,
          ),
          casePageSize,
          filteredCases.length,
          casePageIndex,
        );
      }
      return normalisePage<OperationCase>(
        (
          await api.cases(context, {
            ...caseFilterParams(caseFilters),
            pageIndex: casePageIndex,
            pageSize: casePageSize,
          })
        ).data,
      );
    },
    enabled: !isRootUser && hasVisibleNavItem('cases'),
  });

  const tasksQuery = useQuery({
    queryKey: ['operations-tasks', context],
    queryFn: async () =>
      isDemo
        ? demoPage(demoTasks, 25)
        : normalisePage<OperationTask>(
            (await api.tasks(context, { pageIndex: 0, pageSize: 25 })).data,
          ),
    enabled: !isRootUser && hasVisibleNavItem('tasks'),
  });

  const reportsQuery = useQuery({
    queryKey: ['operations-report-runs', context],
    queryFn: async () =>
      isDemo
        ? demoReports
        : normaliseList<RegulatoryReportRun>(
            (await api.reportRuns(context, 0, 25)).data,
          ),
    enabled: !isRootUser && hasVisibleNavItem('tasks'),
  });

  const reportDefinitionsQuery = useQuery({
    queryKey: ['operations-report-definitions', context],
    queryFn: async () =>
      isDemo
        ? []
        : normaliseList<RegulatoryReportDefinition>(
            (await api.reportDefinitions(context)).data,
          ),
    enabled: !isRootUser && hasVisibleNavItem('tasks'),
  });

  const reconciliationRunsQuery = useQuery({
    queryKey: [
      'operations-reconciliation-runs',
      context,
      reconciliationPageIndex,
      reconciliationPageSize,
    ],
    queryFn: async () =>
      isDemo
        ? demoReconciliationRuns.slice(
            reconciliationPageIndex * reconciliationPageSize,
            (reconciliationPageIndex + 1) * reconciliationPageSize,
          )
        : normaliseList<ReconciliationRun>(
            (
              await api.reconciliationRuns(
                context,
                reconciliationPageIndex,
                reconciliationPageSize,
              )
            ).data,
          ),
    enabled:
      !isRootUser &&
      hasVisibleNavItem('reconciliation') &&
      reconciliationTab === 'eod',
  });

  const partiesReconciliationRunsQuery = useQuery({
    queryKey: [
      'operations-party-reconciliation-runs',
      context,
      partiesReconciliationPageIndex,
      partiesReconciliationPageSize,
    ],
    queryFn: async () =>
      isDemo
        ? demoReconciliationRuns.slice(
            partiesReconciliationPageIndex * partiesReconciliationPageSize,
            (partiesReconciliationPageIndex + 1) *
              partiesReconciliationPageSize,
          )
        : normaliseList<ReconciliationRun>(
            (
              await api.partyReconciliationRuns(
                context,
                partiesReconciliationPageIndex,
                partiesReconciliationPageSize,
              )
            ).data,
          ),
    enabled:
      !isRootUser &&
      hasVisibleNavItem('reconciliation') &&
      reconciliationTab === 'parties',
  });

  const rolesQuery = useQuery({
    queryKey: ['operations-tenant-roles', context],
    queryFn: async () =>
      isDemo
        ? demoRoles
        : normaliseList<TenantRole>(
            (await api.tenantRoles(context, session.tenantId)).data,
          ),
    enabled: Boolean(session.tenantId) && canManageUsers,
  });

  const usersQuery = useQuery({
    queryKey: ['operations-tenant-users', context],
    queryFn: async () =>
      isDemo
        ? demoPage(demoUsers, 50)
        : normalisePage<TenantUser>(
            (await api.tenantUsers(context, session.tenantId, 0, 50)).data,
          ),
    enabled: Boolean(session.tenantId) && canUseTenantUsers,
  });

  const openCaseDetail = (caseId: string) => {
    setActive('cases');
    setCaseDetailId(caseId);
  };

  const openClientCareDetail = (search: Record<string, string>) => {
    setClientCareInitialSearch(search);
    setActive('clients');
  };

  const view = (() => {
    if (active === 'clients') {
      return (
        <ClientCareView
          context={context}
          isDemo={isDemo}
          onOpenCase={openCaseDetail}
          initialSearch={clientCareInitialSearch}
        />
      );
    }
    if (active === 'accountManagement') {
      return (
        <AccountManagementView
          context={context}
          isDemo={isDemo}
          onOpenClientDetails={openClientCareDetail}
          onOpenCase={openCaseDetail}
        />
      );
    }
    if (active === 'users') {
      if (!canManageUsers) {
        return (
          <Alert severity="warning">
            User Management is only available to Admin users.
          </Alert>
        );
      }
      return (
        <UsersView
          context={context}
          tenantId={session.tenantId}
          roles={rolesQuery.data ?? []}
          users={usersQuery.data}
          usersState={usersQuery}
          isDemo={isDemo}
        />
      );
    }
    if (active === 'configuration') {
      return <ConfigurationView tenantId={session.tenantId} />;
    }
    if (active === 'fees') {
      return <FeeChargesView tenantId={session.tenantId} />;
    }
    if (active === 'broadcastNotices') {
      return (
        <BroadcastNoticesView
          tenantId={session.tenantId}
          actorId={session.actorId || session.username}
        />
      );
    }
    if (active === 'cases') {
      if (caseDetailId) {
        return (
          <CaseDetailPage
            caseId={caseDetailId}
            context={context}
            isDemo={isDemo}
            tenantUsers={usersQuery.data?.items ?? []}
            onBack={() => setCaseDetailId(null)}
          />
        );
      }
      return (
        <CasesView
          context={context}
          filters={caseFilters}
          onFilters={setCaseFilters}
          pageIndex={casePageIndex}
          pageSize={casePageSize}
          onPageChange={setCasePageIndex}
          onPageSizeChange={setCasePageSize}
          cases={casesQuery.data}
          casesState={casesQuery}
          isDemo={isDemo}
          tenantUsers={usersQuery.data?.items ?? []}
          onOpenCase={openCaseDetail}
        />
      );
    }
    if (active === 'kycAml') {
      return (
        <KycAmlWorkspace
          context={context}
          actorRoles={session.actorRoles}
          isDemo={isDemo}
        />
      );
    }
    if (active === 'aml') {
      return (
        <AmlWorkspace
          context={context}
          actorId={session.actorId}
          actorRoles={session.actorRoles}
          isDemo={isDemo}
        />
      );
    }
    if (active === 'tasks') {
      return (
        <TasksReportsView
          context={context}
          tasks={tasksQuery.data}
          reports={reportsQuery.data ?? []}
          reportDefinitions={reportDefinitionsQuery.data ?? []}
          tasksState={tasksQuery}
          reportsState={reportsQuery}
          isDemo={isDemo}
        />
      );
    }
    if (active === 'reconciliation') {
      const isParties = reconciliationTab === 'parties';
      return (
        <Stack spacing={2.5}>
          <Tabs
            value={reconciliationTab}
            onChange={(_, value) => setReconciliationTab(value)}
          >
            <Tab value="eod" label="EOD Reconciliation" />
            <Tab value="parties" label="Parties Reconciliation" />
          </Tabs>
          <ReconciliationView
            context={context}
            runs={
              (isParties
                ? partiesReconciliationRunsQuery.data
                : reconciliationRunsQuery.data) ?? []
            }
            runsState={
              isParties
                ? partiesReconciliationRunsQuery
                : reconciliationRunsQuery
            }
            isDemo={isDemo}
            pageIndex={
              isParties
                ? partiesReconciliationPageIndex
                : reconciliationPageIndex
            }
            pageSize={
              isParties ? partiesReconciliationPageSize : reconciliationPageSize
            }
            onPageChange={
              isParties
                ? setPartiesReconciliationPageIndex
                : setReconciliationPageIndex
            }
            onPageSizeChange={(nextPageSize) => {
              if (isParties) {
                setPartiesReconciliationPageIndex(0);
                setPartiesReconciliationPageSize(nextPageSize);
                return;
              }
              setReconciliationPageIndex(0);
              setReconciliationPageSize(nextPageSize);
            }}
            title={
              isParties
                ? 'Parties Reconciliation Runs'
                : 'EOD Reconciliation Runs'
            }
            emptyTitle={
              isParties ? 'No party reconciliation runs returned' : undefined
            }
            emptyDescription={
              isParties
                ? 'Historical party reconciliation runs will appear here once the API returns data.'
                : undefined
            }
            reportFilePrefix={
              isParties ? 'party-reconciliation-report' : undefined
            }
            downloadReport={
              isParties ? api.downloadPartyReconciliationRunReport : undefined
            }
            mismatchType={isParties ? 'parties' : 'eod'}
          />
        </Stack>
      );
    }
    if (active === 'capitalRaise') {
      if (!privateEquityEnabled) {
        return (
          <Alert severity="info">
            Enable the Private Equity account type in Product Configuration to
            use this workspace.
          </Alert>
        );
      }
      return (
        <CapitalRaiseView
          key={`capital-raise-${capitalRaiseViewKey}`}
          context={context}
          tenantId={session.tenantId}
          tenantUsers={usersQuery.data?.items ?? []}
          currentUser={{
            userId: session.actorId,
            username: session.username,
            givenName: session.givenName,
            familyName: session.familyName,
          }}
        />
      );
    }
    if (active === 'platform') {
      return <PlatformWorkspace />;
    }
    return (
      <OverviewView
        dashboard={dashboardQuery.data}
        dashboardState={{
          ...dashboardQuery,
          isFetching:
            dashboardQuery.isFetching || dashboardOpenCasesQuery.isFetching,
          error: dashboardQuery.error ?? dashboardOpenCasesQuery.error,
        }}
        cases={dashboardOpenCasesQuery.data?.items ?? []}
        tasks={tasksQuery.data?.items ?? []}
        reports={reportsQuery.data ?? []}
        onOpenCase={openCaseDetail}
      />
    );
  })();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '280px 1fr' },
        bgcolor: 'background.default',
        backgroundImage: 'none',
      }}
    >
      <Box
        component="aside"
        sx={{
          position: { lg: 'sticky' },
          top: 0,
          alignSelf: 'start',
          height: { lg: '100vh' },
          borderRight: { lg: '1px solid rgba(139,92,246,0.06)' },
          bgcolor: '#ffffff',
          backgroundImage: 'none',
          backdropFilter: 'blur(18px)',
          boxShadow: { lg: '12px 0 32px rgba(86,52,142,0.06)' },
          p: 2,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 1, py: 1 }}
        >
          <Avatar sx={{ bgcolor: 'primary.main' }}>
            <ShieldCheck size={22} />
          </Avatar>
          <Box>
            <Typography variant="h6">Zeta Ops</Typography>
          </Box>
        </Stack>
        <Divider sx={{ my: 2 }} />
        <Stack spacing={0.75}>
          {visibleNavItems.map((item) => {
            const Icon = item.icon;
            const selected = active === item.key;
            return (
              <Button
                key={item.key}
                startIcon={<Icon size={18} />}
                onClick={() => {
                  if (item.key === 'capitalRaise') {
                    setCapitalRaiseViewKey((current) => current + 1);
                  }
                  setActive(item.key);
                  if (item.key !== 'cases') {
                    setCaseDetailId(null);
                  }
                }}
                color="primary"
                variant="text"
                sx={{
                  justifyContent: 'flex-start',
                  py: 1.15,
                  color: selected ? 'primary.dark' : 'text.secondary',
                  bgcolor: selected ? alpha('#8b5cf6', 0.1) : 'transparent',
                  border: '1px solid',
                  borderColor: selected
                    ? alpha('#8b5cf6', 0.18)
                    : 'transparent',
                  '&:hover': {
                    bgcolor: alpha('#8b5cf6', selected ? 0.14 : 0.06),
                  },
                }}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>
      </Box>

      <Box component="main" sx={{ minWidth: 0 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
          sx={{
            px: { xs: 2, md: 4 },
            py: 3,
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#ffffff',
            backdropFilter: 'blur(18px)',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4">
              {visibleNavItems.find((item) => item.key === active)?.label ??
                'Overview'}
            </Typography>
            <Stack spacing={0.75} sx={{ mt: 0.5 }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  maxWidth: { xs: '100%', md: 420 },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {session.username}
              </Typography>
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                useFlexGap
                flexWrap="wrap"
              >
                {actorRoleLabels.map((role) => (
                  <Chip
                    key={role}
                    label={role}
                    size="small"
                    variant="outlined"
                    sx={{
                      maxWidth: 180,
                      height: 24,
                      bgcolor: alpha('#8b5cf6', 0.07),
                      borderColor: alpha('#8b5cf6', 0.2),
                      '& .MuiChip-label': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      },
                    }}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
            useFlexGap
            flexWrap="wrap"
            sx={{ minWidth: { md: 300 } }}
          >
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="center"
              sx={{
                minWidth: 0,
                px: 1.5,
                py: 0.9,
                border: '1px solid',
                borderColor: alpha('#8b5cf6', 0.14),
                borderRadius: 2,
                bgcolor: alpha('#8b5cf6', 0.06),
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: alpha('#8b5cf6', 0.16),
                  color: 'primary.dark',
                  fontSize: '0.82rem',
                  fontWeight: 800,
                }}
              >
                {displayInitials || 'U'}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', lineHeight: 1.1 }}
                >
                  Welcome back
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    maxWidth: { xs: 220, md: 260 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {displayName}
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<LogOut size={18} />}
              onClick={onLogout}
              sx={{
                minHeight: 52,
                px: 2,
                borderColor: alpha('#8b5cf6', 0.28),
                whiteSpace: 'nowrap',
              }}
            >
              Sign out
            </Button>
          </Stack>
        </Stack>

        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1500 }}>
          {!isRootUser && tenantProfileQuery.isError ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              We could not load tenant platform settings, so some
              tenant-specific areas may be unavailable right now.
            </Alert>
          ) : null}
          <Suspense
            fallback={
              <Stack spacing={2} sx={{ py: 6 }}>
                <LinearProgress />
                <Typography color="text.secondary">
                  Loading workspace...
                </Typography>
              </Stack>
            }
          >
            {view}
          </Suspense>
        </Box>
      </Box>
      <Dialog
        open={idleWarningOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
            onLogout();
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', gap: 1.25, pb: 1 }}
        >
          <LockKeyhole size={22} />
          Session timeout
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              Your session has been inactive for 10 minutes. Continue now to
              refresh your token, or log out.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                placeItems: 'center',
                minHeight: 112,
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha('#64748b', 0.18),
                bgcolor: alpha('#8b5cf6', 0.06),
              }}
            >
              <Typography variant="h3" sx={{ fontWeight: 900, lineHeight: 1 }}>
                {idleWarningSecondsRemaining}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                seconds remaining
              </Typography>
            </Box>
            {refreshSessionMutation.isError ? (
              <Alert severity="error">
                We could not refresh your session. Please log in again.
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={onLogout}
            disabled={refreshSessionMutation.isPending}
          >
            Log out
          </Button>
          <Button
            variant="contained"
            onClick={handleContinueSession}
            disabled={refreshSessionMutation.isPending}
            startIcon={
              refreshSessionMutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <LockKeyhole size={16} />
              )
            }
          >
            {refreshSessionMutation.isPending ? 'Refreshing...' : 'Continue'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
